import { createSlice } from "@reduxjs/toolkit";
import {
  resellingAccountStatusAction,
  resellingConnectAccountAction,
  resellingDisconnectAction,
  resellingConfigAction,
  updateCheckinConfigAction,
  updateCreditConfigAction,
  resellingClientsPaginatedAction,
  resellingOverviewStatsAction,
} from "../../actions/UserActions/ResellingActions";

export interface IBlockingSubscription {
  business_id?: string;
  business_name?: string;
  client_email?: string;
  status?: string;
}

export interface IResellingAccountData {
  _id?: string;
  account_name?: string;
  is_verified?: boolean;
  workspaceId?: string;
  stripe_account_id?: string;
  onboarding_status?: string;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  details_submitted?: boolean;
  default_currency?: string;
  last_synced_at?: string;
  disabled_reason?: string | null;
  createdAt?: string;
  updatedAt?: string;
  pending_balance_cents?: number;
  available_balance_cents?: number;
  dashboard_url?: string | null;
  can_disconnect?: boolean;
  blocking_subscriptions?: IBlockingSubscription[];
}

export interface IResellerConfig {
  _id?: string;
  workspaceId?: string;
  checkin_enabled: boolean;
  resale_price_cents: number;
  trial_days: number;
  require_payment_upfront: boolean;
  credits_enabled: boolean;
  markup_multiplier: number;
  credits_per_action: number;
  total_active_locations?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface IResellingPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface IResellingOverviewData {
  has_data: boolean;
  total_profit_cents: number;
  reseller_revenue_cents: number;
  active_locations: number;
  credits_resold: number;
  profit_by_source: {
    checkins_cents: number;
    credits_cents: number;
  };
  blended_margin_percent: number;
  profit_trend: Array<{
    month: string;
    profit_cents: number;
  }>;
  top_clients: Array<{
    business_id: string;
    business_name: string;
    revenue_cents: number;
    profit_cents: number;
    credits_resold?: number;
  }>;
}

interface IResellingState {
  accountStatusData: IResellingAccountData | null;
  accountStatusLoading: boolean;
  connectAccountLoading: boolean;
  disconnectLoading: boolean;
  disconnectErrorData: any | null;
  configData: IResellerConfig | null;
  configLoading: boolean;
  saveConfigLoading: boolean;
  clientsData: any[] | null;
  clientsPagination: IResellingPagination | null;
  clientsLoading: boolean;
  overviewData: IResellingOverviewData | null;
  overviewLoading: boolean;
}

const initialState: IResellingState = {
  accountStatusData: null,
  accountStatusLoading: false,
  connectAccountLoading: false,
  disconnectLoading: false,
  disconnectErrorData: null,
  configData: null,
  configLoading: false,
  saveConfigLoading: false,
  clientsData: null,
  clientsPagination: null,
  clientsLoading: false,
  overviewData: null,
  overviewLoading: false,
};

const resellingSlice = createSlice({
  name: "reselling",
  initialState,
  reducers: {
    clearDisconnectError: (state) => {
      state.disconnectErrorData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Account Status
      .addCase(resellingAccountStatusAction.pending, (state) => {
        state.accountStatusLoading = true;
      })
      .addCase(resellingAccountStatusAction.fulfilled, (state, action) => {
        state.accountStatusLoading = false;
        state.accountStatusData = action.payload?.data || action.payload;
      })
      .addCase(resellingAccountStatusAction.rejected, (state) => {
        state.accountStatusLoading = false;
      })
      // Connect Account
      .addCase(resellingConnectAccountAction.pending, (state) => {
        state.connectAccountLoading = true;
      })
      .addCase(resellingConnectAccountAction.fulfilled, (state) => {
        state.connectAccountLoading = false;
      })
      .addCase(resellingConnectAccountAction.rejected, (state) => {
        state.connectAccountLoading = false;
      })
      // Disconnect Account
      .addCase(resellingDisconnectAction.pending, (state) => {
        state.disconnectLoading = true;
        state.disconnectErrorData = null;
      })
      .addCase(resellingDisconnectAction.fulfilled, (state) => {
        state.disconnectLoading = false;
        state.disconnectErrorData = null;
        state.accountStatusData = null;
      })
      .addCase(resellingDisconnectAction.rejected, (state, action) => {
        state.disconnectLoading = false;
        state.disconnectErrorData = action.payload;
      })
      // Reselling Config
      .addCase(resellingConfigAction.pending, (state) => {
        state.configLoading = true;
      })
      .addCase(resellingConfigAction.fulfilled, (state, action) => {
        state.configLoading = false;
        state.configData = action.payload?.data || action.payload;
      })
      .addCase(resellingConfigAction.rejected, (state) => {
        state.configLoading = false;
      })
      // Update Checkin Config
      .addCase(updateCheckinConfigAction.pending, (state) => {
        state.saveConfigLoading = true;
      })
      .addCase(updateCheckinConfigAction.fulfilled, (state, action) => {
        state.saveConfigLoading = false;
        const newData = action.payload?.data || action.payload;
        if (newData) {
          state.configData = { ...(state.configData || {}), ...newData };
        }
      })
      .addCase(updateCheckinConfigAction.rejected, (state) => {
        state.saveConfigLoading = false;
      })
      // Update Credit Config
      .addCase(updateCreditConfigAction.pending, (state) => {
        state.saveConfigLoading = true;
      })
      .addCase(updateCreditConfigAction.fulfilled, (state, action) => {
        state.saveConfigLoading = false;
        const newData = action.payload?.data || action.payload;
        if (newData) {
          state.configData = { ...(state.configData || {}), ...newData };
        }
      })
      .addCase(updateCreditConfigAction.rejected, (state) => {
        state.saveConfigLoading = false;
      })
      // Reselling Clients Paginated
      .addCase(resellingClientsPaginatedAction.pending, (state) => {
        state.clientsLoading = true;
      })
      .addCase(resellingClientsPaginatedAction.fulfilled, (state, action) => {
        state.clientsLoading = false;
        state.clientsData = action.payload?.data?.clients || [];
        state.clientsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(resellingClientsPaginatedAction.rejected, (state) => {
        state.clientsLoading = false;
      })
      // Overview Stats
      .addCase(resellingOverviewStatsAction.pending, (state) => {
        state.overviewLoading = true;
      })
      .addCase(resellingOverviewStatsAction.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overviewData = action.payload?.data || action.payload;
      })
      .addCase(resellingOverviewStatsAction.rejected, (state) => {
        state.overviewLoading = false;
      });
  },
});

export const { clearDisconnectError } = resellingSlice.actions;
export default resellingSlice.reducer;
